import { styled } from "@iventis/styles";
import { InteractiveElement } from "@iventis/styles/src/components";
import { borderRadius } from "@iventis/styles/src/atomic-rules";
import React, { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Theme } from "@emotion/react";
import { SimpleTooltip } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import { RootDispatch, RootState } from "./state/root.store";
import { useMapBackground, useMapBackgrounds, useMapBackgroundTags } from "./hooks/use-map-backgrounds";
import { map as mapSlice } from "./state/map.slice";
import { buildCategoryCards, getSelectedBackgroundCategory, NONE_TAG } from "./background-selector-helpers";

export const BackgroundSelectorPreviewComponent: FunctionComponent<{ onClick: () => void; className?: string; onSave?: (backgroundId: string) => void }> = ({
    onClick,
    className,
    onSave,
}) => {
    const translate = useIventisTranslate();
    const dispatch = useDispatch<RootDispatch>();

    const { selectedMapBackgroundId } = useSelector((s: RootState) => ({ selectedMapBackgroundId: s.mapReducer.mapModule.selectedMapBackgroundId }));
    const { mapBackgrounds } = useMapBackgrounds();
    const { mapBackgroundTags } = useMapBackgroundTags();
    const { mapBackground } = useMapBackground(selectedMapBackgroundId);

    /** Which category tag the currently selected background belongs to (if any). */
    const selectedCategory = useMemo<string | null>(() => {
        if (!mapBackgrounds || !mapBackgroundTags || !selectedMapBackgroundId) return null;
        return getSelectedBackgroundCategory(selectedMapBackgroundId, mapBackgrounds, mapBackgroundTags);
    }, [mapBackgrounds, mapBackgroundTags, selectedMapBackgroundId]);

    /** One entry per category that has at least one background asset.
     *  Order follows the tag order returned by the API.
     *  Thumbnail priority: selected background (if in category) > metaData.default > first asset. */
    const categoryCards = useMemo(() => {
        if (!mapBackgrounds || !mapBackgroundTags) return [];
        return buildCategoryCards(mapBackgrounds, mapBackgroundTags, selectedMapBackgroundId);
    }, [mapBackgrounds, mapBackgroundTags, selectedMapBackgroundId]);

    const handleCategoryClick = (assetId: string) => {
        dispatch(mapSlice.actions.setMapBackground(assetId));
        onSave?.(assetId);
    };

    const backgroundName = mapBackground?.name ?? "";
    const displayLabel = selectedCategory ? (
        <span>
            {selectedCategory} <i>({backgroundName})</i>
        </span>
    ) : (
        <span>{backgroundName}</span>
    );

    return (
        <Container className={className} data-testid="map-background-selector">
            {/* Left: selected background name with category */}
            {/* Bug 21514 - do not translate this element as it causes a crash */}
            <BackgroundName data-testid="background-selected-name" translate="no">
                {displayLabel}
            </BackgroundName>

            {/* Right: category thumbnail cards + settings */}
            <RightSection>
                <CategoryRow>
                    {categoryCards.map(({ category, asset }) => (
                        <SimpleTooltip key={category} text={category} placement="top">
                            <CategoryThumbnailButton type="button" onClick={() => handleCategoryClick(asset.id)} data-testid={`quick-background-${category}`}>
                                <CategoryThumbnail imageUrl={category === NONE_TAG ? undefined : asset.thumbnailUrl} isSelected={selectedCategory === category}>
                                    {category === NONE_TAG && <FontAwesomeIcon icon={["far", "ban"]} />}
                                </CategoryThumbnail>
                            </CategoryThumbnailButton>
                        </SimpleTooltip>
                    ))}
                </CategoryRow>
                <SimpleTooltip text={translate(Content.map3.backgroundMapSelector.configure)} placement="top">
                    <SettingsButton type="button" onClick={onClick} data-testid="quick-background-settings">
                        <FontAwesomeIcon icon={["far", "sliders"]} />
                    </SettingsButton>
                </SimpleTooltip>
            </RightSection>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 16px;
    gap: 10px;
`;

const BackgroundName = styled.span`
    font-weight: 500;
    font-size: 13px;
    color: ${({ theme }) => (theme as Theme).shades.one};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

const RightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const SettingsButton = styled(InteractiveElement)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: ${borderRadius.standard};
    color: ${({ theme }) => (theme as Theme).shades.two};

    &:hover {
        color: ${({ theme }) => (theme as Theme).shades.one};
        background-color: ${({ theme }) => (theme as Theme).otherColors.hover};
    }
`;

const CategoryRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-shrink: 0;
`;

const CategoryThumbnailButton = styled(InteractiveElement)`
    padding: 0;
    line-height: 0;
`;

const CategoryThumbnail = styled.div<{ imageUrl?: string; isSelected: boolean }>`
    position: relative;
    width: 28px;
    height: 28px;
    border-radius: ${borderRadius.standard};
    border: 2px solid ${({ isSelected, theme }) => (isSelected ? (theme as Theme).primaryColors.focus : (theme as Theme).shades.four)};

    background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : "none")};
    background-color: ${({ theme }) => (theme as Theme).shades.five};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: ${({ theme }) => (theme as Theme).shades.two};
        font-size: 12px;
    }
`;
